import { supabase } from "@/platform/lib/supabase";
import { CURSO_WEIGHTS, type FuvestDiscipline, type UspCurso } from "@/tenants/usp/lib/nota-projetada-fuvest";

/**
 * Plano adaptativo Fuvest. Prioridade por disciplina =
 *   peso_na_prova × lacuna_do_aluno × peso_do_curso
 *
 * peso_na_prova: distribuição histórica 1ª fase (frequência).
 * lacuna: 1 - taxa_acerto do simulado mais recente (default 0.5 sem dados).
 * peso_do_curso: CURSO_WEIGHTS da 2ª fase (Mat 4, Fís 3… para Eng Produção).
 */

// Frequência relativa 1ª fase (do corpus 268 obj)
const EXAM_FREQUENCY: Record<string, number> = {
  geografia: 0.126,
  fisica: 0.115,
  matematica: 0.107,
  quimica: 0.104,
  historia: 0.096,
  biologia: 0.089,
  literatura: 0.085,
  ingles: 0.07,
  sociologia: 0.059,
  portugues: 0.048,
  filosofia: 0.03,
  artes: 0.022,
};

const DISCIPLINE_LABELS: Record<string, string> = {
  matematica: "Matemática",
  fisica: "Física",
  quimica: "Química",
  biologia: "Biologia",
  portugues: "Português",
  literatura: "Literatura",
  ingles: "Inglês",
  historia: "História",
  geografia: "Geografia",
  filosofia: "Filosofia",
  sociologia: "Sociologia",
  artes: "Artes",
};

export interface PlanBlock {
  discipline: string;
  label: string;
  priority: number; // 0-100 normalizado
  reason: string;
  suggestedHours: number;
  accuracy: number | null; // taxa de acerto observada (null se sem dados)
}

export interface WeeklyPlan {
  weekStart: string;
  totalHours: number;
  blocks: PlanBlock[];
  basedOnSessionId: string | null;
}

function currentWeekStart(now = new Date()): string {
  const d = new Date(now);
  d.setHours(0, 0, 0, 0);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  return d.toISOString().slice(0, 10);
}

export async function generateUspPlan(userId: string): Promise<WeeklyPlan> {
  // 1. Perfil (curso + horas)
  const { data: profile } = await supabase
    .from("usp_profiles")
    .select("curso_alvo, weekly_hours")
    .eq("user_id", userId)
    .maybeSingle();
  const curso = (profile?.curso_alvo as UspCurso | undefined) ?? "engenharia_producao";
  const weeklyHours = Number(profile?.weekly_hours ?? 20);
  const cursoCfg = CURSO_WEIGHTS[curso];

  // 2. Simulado mais recente finalizado → by_discipline
  const { data: lastSession } = await supabase
    .from("usp_sessions")
    .select("id, metadata")
    .eq("user_id", userId)
    .eq("kind", "simulation_1f")
    .not("finished_at", "is", null)
    .order("finished_at", { ascending: false })
    .limit(1)
    .maybeSingle();

  const byDisc =
    (lastSession?.metadata as { by_discipline?: Record<string, { total: number; correct: number }> } | null)
      ?.by_discipline ?? {};

  // 3. Calcula prioridade por disciplina
  const disciplines = Object.keys(EXAM_FREQUENCY);
  const raw = disciplines.map((d) => {
    const freq = EXAM_FREQUENCY[d] ?? 0.05;
    const stat = byDisc[d];
    const accuracy = stat && stat.total > 0 ? stat.correct / stat.total : null;
    const lacuna = accuracy === null ? 0.5 : 1 - accuracy;
    const cursoWeight = (cursoCfg.disciplines[d as FuvestDiscipline] ?? 1) / 4; // normaliza 0-1
    const score = freq * lacuna * (0.5 + cursoWeight);
    return { discipline: d, freq, accuracy, lacuna, score };
  });

  const maxScore = Math.max(...raw.map((r) => r.score), 0.0001);

  const blocks: PlanBlock[] = raw
    .map((r) => {
      const priority = Math.round((r.score / maxScore) * 100);
      const reasonParts: string[] = [];
      if (r.freq >= 0.1) reasonParts.push("peso alto na prova");
      if (r.accuracy !== null && r.accuracy < 0.5) reasonParts.push(`acerto baixo (${Math.round(r.accuracy * 100)}%)`);
      if (r.accuracy === null) reasonParts.push("sem diagnóstico ainda");
      const cw = cursoCfg.disciplines[r.discipline as FuvestDiscipline] ?? 1;
      if (cw >= 3) reasonParts.push("peso forte no seu curso");
      return {
        discipline: r.discipline,
        label: DISCIPLINE_LABELS[r.discipline] ?? r.discipline,
        priority,
        reason: reasonParts.join(" · ") || "manutenção",
        accuracy: r.accuracy,
        suggestedHours: 0, // preenchido depois
      };
    })
    .sort((a, b) => b.priority - a.priority);

  // 4. Distribui horas proporcional à prioridade (top 8 disciplinas)
  const top = blocks.slice(0, 8);
  const prioritySum = top.reduce((s, b) => s + b.priority, 0) || 1;
  for (const b of top) {
    b.suggestedHours = Number(((b.priority / prioritySum) * weeklyHours).toFixed(1));
  }

  return {
    weekStart: currentWeekStart(),
    totalHours: weeklyHours,
    blocks: top,
    basedOnSessionId: (lastSession?.id as string | undefined) ?? null,
  };
}
